import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import Login from './pages/Login';
import CandidateDashboard from './pages/CandidateDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminLayout from './components/AdminLayout';
import AdminManagement from './pages/AdminManagement';
import UserManagement from './pages/UserManagement';
import CandidateManagement from './pages/CandidateManagement';
import ScenarioManagement from './pages/ScenarioManagement';
import CandidateEvaluation from './pages/CandidateEvaluation';
import CandidateResult from './pages/CandidateResult';
import ExamPortal from './pages/ExamPortal';
import TestLogin from './pages/TestLogin';
import SessionPage from './pages/SessionPage';
import About from './pages/About';

function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null; // Wait for auth to finish (MSAL redirect processing)
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <AdminLayout>{children}</AdminLayout>;
}

function SuperAdminRoute({ children }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const role = (user?.role || '').toLowerCase();
  if (role !== 'superadmin') return <Navigate to="/admin/dashboard" replace />;
  return <AdminLayout>{children}</AdminLayout>;
}

function RoleRouter() {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  if (role === 'superadmin') {
    return <Navigate to="/sadmin/dashboard" replace />;
  }
  if (role === 'admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }
  // Candidates go directly to exam portal
  return <Navigate to="/examportal" replace />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <RoleRouter />
          </ProtectedRoute>
        }
      />

      {/* Admin routes with sidebar layout */}
      <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/users" element={<AdminRoute><UserManagement /></AdminRoute>} />
      <Route path="/admin/candidates" element={<AdminRoute><CandidateManagement /></AdminRoute>} />
      <Route path="/admin/questions" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/admin/scenarios" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/admin/evaluation" element={<AdminRoute><CandidateEvaluation /></AdminRoute>} />
      <Route path="/admin/evaluation/result" element={<AdminRoute><CandidateResult /></AdminRoute>} />
      <Route path="/admin/about" element={<AdminRoute><About /></AdminRoute>} />

      {/* Superadmin routes (all admin routes + user/admin management) */}
      <Route path="/sadmin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/sadmin/users" element={<AdminRoute><UserManagement /></AdminRoute>} />
      <Route path="/sadmin/admins" element={<AdminRoute><AdminManagement /></AdminRoute>} />
      <Route path="/sadmin/candidates" element={<AdminRoute><CandidateManagement /></AdminRoute>} />
      <Route path="/sadmin/scenarios" element={<SuperAdminRoute><ScenarioManagement /></SuperAdminRoute>} />
      <Route path="/sadmin/questions" element={<Navigate to="/sadmin/scenarios" replace />} />
      <Route path="/sadmin/evaluation" element={<AdminRoute><CandidateEvaluation /></AdminRoute>} />
      <Route path="/sadmin/evaluation/result" element={<AdminRoute><CandidateResult /></AdminRoute>} />
      <Route path="/sadmin/about" element={<AdminRoute><About /></AdminRoute>} />

      {/* Candidate routes */}
      <Route
        path="/examportal"
        element={
          <ProtectedRoute>
            <ExamPortal />
          </ProtectedRoute>
        }
      />
      <Route path="/examportal/session/:sessionId" element={<SessionPage />} />

      {/* Test routes (dev/staging direct login) */}
      <Route path="/test-login" element={<TestLogin />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
